import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, Coins } from "lucide-react";

const MAX_ATTEMPTS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const { t } = useLanguage();
  const [state, setState] = useState("checking");
  const [creditsAdded, setCreditsAdded] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setCreditsAdded(data.credits_added);
          setState("success");
          refreshUser();
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) setState("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="relative z-10 mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-6 text-center">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
          <h1 className="mt-6 font-heading text-3xl font-black">{t("payment.checking")}</h1>
          <p className="mt-2 text-muted-foreground">{t("payment.checkingSub")}</p>
        </div>
      )}
      {state === "success" && (
        <div data-testid="payment-success" className="animate-rise">
          <CheckCircle2 className="mx-auto h-16 w-16 text-truth" />
          <h1 className="mt-6 font-heading text-4xl font-black tracking-tighter">{t("payment.success")}</h1>
          <p className="mt-3 flex items-center justify-center gap-2 text-lg text-credit">
            <Coins className="h-5 w-5" /> {t("payment.added", { n: creditsAdded })}
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Button asChild className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
              <Link to="/wiki">{t("payment.goBet")}</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-md border-white/20 bg-transparent hover:bg-white/10">
              <Link to="/credits">{t("payment.buyMore")}</Link>
            </Button>
          </div>
        </div>
      )}
      {state === "pending" && (
        <div data-testid="payment-pending">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-credit" />
          <h1 className="mt-6 font-heading text-3xl font-black">{t("payment.pending")}</h1>
          <p className="mt-2 text-muted-foreground">{t("payment.pendingSub")}</p>
          <Button asChild className="mt-8 rounded-md bg-primary font-bold uppercase tracking-wider"><Link to="/credits">{t("payment.back")}</Link></Button>
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-error">
          <XCircle className="mx-auto h-16 w-16 text-lie" />
          <h1 className="mt-6 font-heading text-3xl font-black">{t("payment.failed")}</h1>
          <p className="mt-2 text-muted-foreground">{t("payment.failedSub")}</p>
          <Button asChild className="mt-8 rounded-md bg-primary font-bold uppercase tracking-wider"><Link to="/credits">{t("payment.retry")}</Link></Button>
        </div>
      )}
    </main>
  );
}
